import { useQueries, useQuery } from "@tanstack/react-query";
import { isString } from "lodash";
import { useMemo, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import {
  fetchApplication,
  fetchRecordById,
  fetchRecordByIndex,
  fetchRecordsTextJson,
  type RecordTextJson,
} from "../../api/client";
import type { Application, BaseRecord } from "./../../api/types";
import type { CanvasPaperConfig } from "../../designer/PageDesigner/PageDesigner";
import { useFilestack } from "../../hooks/useFilestack";
import type { RenderContext } from "./../renderer/fabric-renderer";
import { getItemsByType } from "./items";
import {
  DocumentDesignItemType,
  type DocumentDesignState,
  type LinkedRecordItem,
} from "./types";

export function useRenderContext(
  state: DocumentDesignState,
  appId: string,
  recordIdOrIndex: number | string,
  dimensions: CanvasPaperConfig,
): RenderContext {
  const filestack = useFilestack();

  const applicationResult = useQuery({
    queryKey: ["application", appId],
    queryFn: () => fetchApplication(appId),
  });

  const recordByIndexResult = useQuery({
    enabled: typeof recordIdOrIndex === "number",
    queryKey: ["record", appId, recordIdOrIndex],
    queryFn: () => fetchRecordByIndex(appId, Number(recordIdOrIndex)),
  });

  // Queries for the doc designer record to use as data source
  const recordByIdResult = useQuery({
    enabled: typeof recordIdOrIndex === "string",
    queryKey: ["record", appId, recordIdOrIndex],
    queryFn: () => fetchRecordById(appId, String(recordIdOrIndex)),
  });

  const record = recordByIndexResult.data?.items?.[0] ?? recordByIdResult.data;
  const totalRecords =
    typeof recordIdOrIndex === "number"
      ? (recordByIndexResult.data?.total ?? 0)
      : +!!recordByIdResult?.data;

  const recordsTextJsonResult = useQuery({
    enabled: applicationResult.isSuccess && !!record,
    queryKey: ["recordsTextJson", appId, record?.id],
    queryFn: () =>
      fetchRecordsTextJson(
        appId,
        [
          "id",
          ...(applicationResult.data?.structure.map((field) => field.slug) ??
            []),
        ],
        record?.id ? [record?.id] : [],
      ),
  });

  const recordTextJson = useMemo(
    () => recordsTextJsonResult.data?.find((item) => item.id === record?.id),
    [recordsTextJsonResult.data, record],
  );

  // Finds all items in canvas that are linked records
  const linkedRecordItems: LinkedRecordItem[] = useMemo(
    () =>
      getItemsByType(
        state,
        DocumentDesignItemType.linkedRecord,
      ) as LinkedRecordItem[],
    [state],
  );

  // get all application IDs being pointed by those fields
  const linkedApplicationsIds = useMemo(
    () => [
      ...new Set(
        linkedRecordItems.map((item) => item.field_linked_application),
      ),
    ],
    [linkedRecordItems],
  );

  const { linkedApplications, loading: loadingLinkedApplications } =
    useLinkedApplications(linkedApplicationsIds);

  const {
    linkedRecords,
    loading: loadingLinkedRecords,
    allFetched: allLinkedRecordsFetched,
  } = useLinkedRecords(record, linkedRecordItems, linkedApplications);

  // this is used mostly to know when we can call `rearrangeItems`
  const loading =
    applicationResult.isLoading ||
    recordsTextJsonResult.isLoading ||
    loadingLinkedApplications ||
    loadingLinkedRecords;

  return useMemo(() => {
    return {
      data: recordTextJson ?? {},
      linkedApplications,
      linkedRecords,
      record,
      totalRecords,
      application: applicationResult.data,
      filestack,
      loading,
      allLinkedRecordsFetched,
      dimensions,
    };
  }, [
    linkedApplications,
    linkedRecords,
    recordTextJson,
    applicationResult.data,
    record,
    filestack,
    loading,
    allLinkedRecordsFetched,
    totalRecords,
    dimensions,
  ]);
}

export function useLinkedApplications(applicationIds: string[]) {
  const [linkedApplicationsMap, setLinkedApplicationsMap] = useState<
    Record<string, Application>
  >({});

  const linkedApplicationResults = useQueries({
    queries: applicationIds.map((appId) => ({
      queryKey: ["linkedApplications", appId],
      queryFn: () => fetchApplication(appId),
    })),
  });

  const resultsData = useMemo(
    () => linkedApplicationResults.map((result) => result.data),
    [linkedApplicationResults],
  );

  // useDeepCompareEffect is necessary here to avoid re-creating the map object thus causing entire renderContext to be recreated on every render
  useDeepCompareEffect(() => {
    const linkedApplications: Record<string, Application> =
      linkedApplicationResults
        .filter((result) => result.isSuccess && result.data)
        .reduce(
          (acc, result) => ({ ...acc, [result.data!.id]: result.data }),
          {},
        );

    setLinkedApplicationsMap(linkedApplications);
  }, [resultsData]);

  const loading = useMemo(
    () => linkedApplicationResults.some((result) => result.isLoading),
    [linkedApplicationResults],
  );

  return { linkedApplications: linkedApplicationsMap, loading };
}

export function useLinkedRecords(
  record: BaseRecord | undefined,
  linkedRecordItems: LinkedRecordItem[],
  linkedApplications: Record<string, Application | undefined>,
) {
  const [linkedRecordsMap, setLinkedRecordsMap] = useState<
    Record<string, RecordTextJson[]>
  >({});

  // Queries individual data for each linked record item using its own settings and filters
  const linkedRecordsResults = useQueries({
    queries: linkedRecordItems.map((item) => ({
      queryKey: [
        "linkedRecords",
        item.field_linked_application,
        item.id,
        item.field_slug,
        item.field_list,
        record?.id,
        item.filter,
        item.sort,
        linkedApplications[item.field_linked_application]?.structure,
      ],
      enabled: !!record && !!linkedApplications[item.field_linked_application],
      queryFn: () => {
        const visibleFields = [
          "id",
          ...item.field_list
            .map((field) => (isString(field) ? field : field.field))
            .filter((field) =>
              linkedApplications[item.field_linked_application]?.structure.find(
                (appField) => appField.slug === field,
              ),
            ),
        ];

        const linkedRecordIds = record?.[item.field_slug] as string[];
        if (!linkedRecordIds.length) {
          return [];
        }

        return fetchRecordsTextJson(
          item.field_linked_application,
          visibleFields,
          linkedRecordIds,
          item.filter,
          item.sort,
        );
      },
    })),
  });

  const allFetched =
    linkedRecordItems.length === 0 ||
    linkedRecordsResults.every(({ isFetched }) => isFetched);

  const resultsData = useMemo(
    () => linkedRecordsResults.map((result) => result.data),
    [linkedRecordsResults],
  );

  // useDeepCompareEffect is necessary here to avoid re-creating the map object thus causing entire renderContext to be recreated on every render
  useDeepCompareEffect(() => {
    // Generates a map to store the results of the linked records queries.
    // Key is the id of the item on the canvas. This allows us to have multiple items
    // using the same linked record, but with individual configs
    // Value is a list of object with the JSON data we want to render in that item.
    const linkedRecords: Record<string, RecordTextJson[]> = linkedRecordsResults
      .filter((result) => result.isSuccess && result.data)
      .reduce(
        (acc, result, index) => ({
          ...acc,
          [linkedRecordItems[index].id]: result.data,
        }),
        {},
      );

    setLinkedRecordsMap(linkedRecords);
  }, [resultsData]);

  const loading = useMemo(
    () => linkedRecordsResults.some((result) => result.isLoading),
    [linkedRecordsResults],
  );

  return { linkedRecords: linkedRecordsMap, loading, allFetched };
}
